import { useEditContext } from '@components/utils/contexts/EditContext';
import Icon from '@components/utils/icons/icon';
import { ROUTE_PRODUCT } from '@constants';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { useEmbedContext } from '@lib/embed/providers/EmbedProvider';
import localeDirection from '@utils/locale/direction';
import { useRouter } from 'next/router';
import { FC, useCallback, useEffect, useMemo, useRef } from 'react';

import { useNavigationContext } from '../contexts/NavigationContext';
import {
    useProgressContext,
    useProgressDispatchContext,
} from '../contexts/ProgressContext';

const Overlay = styled.div`
    z-index: 900;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    position: absolute;
    display: flex;
    align-items: end;
    justify-content: space-between;
    cursor: pointer;
    user-select: none;
    padding: ${({ theme }) => theme.spacing.sm};
`;

const LeftIcon = styled(Icon)`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: ${({ theme }) => theme.spacing.xs};
`;

const RightIcon = styled(Icon)`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: ${({ theme }) => theme.spacing.xs};
`;

type Props = {
    onPlay: () => void;
    onPause: () => void;
};

const NavigationOverlay: FC<Props> = ({ onPlay, onPause }) => {
    const router = useRouter();
    const { palette } = useTheme();
    const {
        pathname,
        query: { company, product, story_order },
    } = router;

    const { isEditMode } = useEditContext();
    const { isEmbedContextLoading, isStoryBundle } = useEmbedContext();
    const { previousStory, nextStory, module } = useNavigationContext();
    const { currentPageIndex, numberOfPages, isPaused } = useProgressContext();
    const dispatch = useProgressDispatchContext();

    const timeoutRef = useRef<NodeJS.Timeout>();
    const isRtl = localeDirection(router.locale) === 'rtl';

    const canEscapeStory = useMemo(() => {
        return !isEmbedContextLoading && !isStoryBundle;
    }, [isEmbedContextLoading, isStoryBundle]);

    const isFirstStoryPage = useMemo(
        () => currentPageIndex === 0,
        [currentPageIndex]
    );

    const isLastStoryPage = useMemo(
        () => currentPageIndex + 1 === numberOfPages,
        [currentPageIndex, numberOfPages]
    );

    const showChevronLeft = useMemo(() => {
        if (!isFirstStoryPage) return true;
        return !!previousStory;
    }, [isFirstStoryPage, previousStory]);

    const showChevronRight = useMemo(() => {
        if (!isLastStoryPage) return true;
        if (nextStory) return true;
        return canEscapeStory;
    }, [isLastStoryPage, nextStory, canEscapeStory]);

    const previous = useCallback(() => {
        if (currentPageIndex === 0 && previousStory) {
            return router.push(
                {
                    pathname,
                    query: {
                        company,
                        product,
                        module,
                        frame: -1,
                        slug: previousStory.Slug,
                        story_order: (Number(story_order) || 0) - 1,
                    },
                },
                `/${product}/story/${previousStory.Slug}`
            );
        }

        dispatch({ type: 'PREVIOUS_PAGE' });
    }, [
        company,
        currentPageIndex,
        module,
        pathname,
        previousStory,
        product,
        router,
        story_order,
        dispatch,
    ]);

    const next = useCallback(() => {
        if (!isLastStoryPage) {
            return dispatch({ type: 'NEXT_PAGE' });
        }

        if (nextStory) {
            return router.push(
                {
                    pathname,
                    query: {
                        company,
                        product,
                        module,
                        slug: nextStory.Slug,
                        story_order: Number(story_order) + 1,
                    },
                },
                `/${product}/story/${nextStory.Slug}#next`
            );
        }

        if (!canEscapeStory) return;

        return router.push(
            { pathname: ROUTE_PRODUCT, query: { company, product } },
            `/${product}`
        );
    }, [
        canEscapeStory,
        company,
        isLastStoryPage,
        module,
        nextStory,
        pathname,
        product,
        router,
        story_order,
        dispatch,
    ]);

    const handleKeyDown = useCallback(
        async (e: KeyboardEvent) => {
            if (isEditMode) return;

            if (e.key === 'ArrowLeft') {
                isRtl ? next() : previous();
            } else if (e.key === 'ArrowRight') {
                isRtl ? previous() : next();
            } else if (e.key === 'Escape') {
                router.back();
            }
        },
        [isEditMode, router, isRtl, previous, next]
    );

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);

    const debouncePause = (e: React.MouseEvent | React.TouchEvent) => {
        e.preventDefault();

        if (e.type === 'touchstart') {
            e = e as React.TouchEvent;
        }

        timeoutRef.current = setTimeout(() => {
            onPause();
        }, 500);
    };

    const mouseUpFullPage = async (e: React.MouseEvent | React.TouchEvent) => {
        e.preventDefault();
        timeoutRef.current && clearTimeout(timeoutRef.current);

        if (isPaused) {
            onPlay();
        } else {
            let clickX: number;

            if (e.type === 'mouseup') {
                e = e as React.MouseEvent;
                clickX = e.clientX;
            } else {
                e = e as React.TouchEvent;
                clickX = e.changedTouches[0].clientX;
            }

            if (window.innerWidth / 2 - clickX < 0) {
                isRtl ? previous() : next();
            } else {
                isRtl ? next() : previous();
            }
        }
    };

    if (isEditMode) return null;

    return (
        <Overlay
            onTouchStart={debouncePause}
            onMouseDown={debouncePause}
            onTouchEnd={mouseUpFullPage}
            onMouseUp={mouseUpFullPage}
        >
            {showChevronLeft && (
                <LeftIcon
                    name='chevron_left'
                    size='medium'
                    color={palette.common.white}
                />
            )}
            {showChevronRight && (
                <RightIcon
                    name='chevron_right'
                    size='medium'
                    color={palette.common.white}
                />
            )}
        </Overlay>
    );
};

export default NavigationOverlay;
